/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

function UsersEditViewController(
    this: JS5Object,
    _: JS5Object,
    $location: JS5Object,
    $q: JS5Object,
    $route: JS5Object,
    $routeParams: JS5Object,
    $timeout: JS5Function,
    uiGridConstants: JS5Object,
    BreadcrumbService: JS5Object,
    GroupsService: JS5Object,
    ModalService: JS5Object,
    NotificationService: JS5Object,
    PermissionsService: JS5Object,
    SidebarService: JS5Object,
    UsersService: JS5Object,
) {
    const EMPTY_STRING = '';
    this.editData = {};
    this.emailUsernameInput = '';
    this.passwordInput = '';
    this.retypedPasswordInput = '';
    this.requirePassword = $routeParams.id === '-1';

    this.passwordValid = false;
    this.retypedPasswordValid = false;

    const getAvailableGroups = () => {
        let criteria = {
            fetchLimit: 0,
            includeHidden: false
        };
        return $q.all({
            groups: GroupsService.getAllGroups(criteria),
            isSystemOps: PermissionsService.isSystemOperator()
        }).then((data: JS5Object) => {
            let groupsById = _.keyBy(data.groups, 'id');
            let writeGroupPromises: JS5Object[] = [];
            _.forEach(groupsById, (group: JS5Object) => {
                if (!data.isSystemOps) {
                    writeGroupPromises.push(GroupsService.isSystemOperatorGroup(group.id).then((isSystemOpsOnly: boolean) => {
                        if (isSystemOpsOnly) {
                            delete groupsById[group.id];
                        }
                    }));
                }
            });
            return $q.all(writeGroupPromises).then(() => {
                this.availableGroups = _.values(groupsById);
            });
        });
    };

    const getData = () => {
        return UsersService.getUser($routeParams.id).then((user: JS5Object) => {
            UsersService.originalEditData = _.cloneDeep(user);
            this.editData = user;
            this.emailUsernameInput = _.get(user, 'username');
            this.gridOptions.data = _.toArray(_.get(this.editData, 'roles'));
        });
    };

    this.retypedPasswordRequirements = [{
        getDescription: () => {
            let thisRequirement = this.retypedPasswordRequirements[0];
            if (thisRequirement.isValid()) {
                return `Passwords match`;
            } else if (thisRequirement.isWarning()) {
                return `Passwords empty`;
            } else {
                return `Passwords don't match`;
            }
        },
        isValid: () => this.passwordInput === this.retypedPasswordInput && !!this.passwordInput.length,
        isWarning: () => !this.passwordInput.length && !this.retypedPasswordInput.length
    }];

    const saveHandler = () => {
        this.loadingIndicator.show = true;
        _.set(this.editData, 'roles', _.unionBy(_.get(this.editData, 'roles'), this.gridOptions.data, 'id'));
        _.set(this.editData, 'username', this.emailUsernameInput);
        _.set(this.editData, 'emailAddress', this.emailUsernameInput);
        if (this.requirePassword) {
            _.set(this.editData, 'password', _.get(this, 'passwordInput'));
        }
        if ($routeParams.id === '-1') {
            saveNewUser();
        } else {
            updateUser();
        }
    };



    const updateUser = () => {
        UsersService.updateUser(this.editData, UsersService.originalEditData.roles).then((user: JS5Object) => {
            UsersService.originalEditData = _.cloneDeep(user);
            this.editData = user;
            this.editorForm.$setPristine();
            this.gridApi.selection.clearSelectedRows();
            NotificationService.createSuccessNotification('User updated successfully');
        }).catch((error: JS5Object) => {
            _.unset(this.editData, 'password');
            NotificationService.createFailureNotification(_.get(error, 'data.message'));
        }).finally(() => {
            resetPasswordFields();
            this.loadingIndicator.show = false;
        });
    };

    const saveNewUser = () => {
        UsersService.createUser(this.editData).then((user: JS5Object) => {
            $route.updateParams({id: user.id});
            this.gridApi.selection.clearSelectedRows();
            NotificationService.createSuccessNotification('User created successfully');
        }).catch((error: JS5Object) => {
            _.unset(this.editData, 'password');
            NotificationService.createFailureNotification(_.get(error, 'data.message'));
        }).finally(() => {
            this.loadingIndicator.show = false;
        });
    };

    const addUserToGroupsSelectionHandler = (selectedGroups: Array<JS5Object>) => {
        let originalGroupSize = _.size(this.gridOptions.data);
        this.gridOptions.data = _.unionBy(this.gridOptions.data, selectedGroups, 'id');
        if (_.size(this.gridOptions.data) > originalGroupSize) {
            this.editorForm.$setDirty();
        }
        this.loadingIndicator.show = false;
    };

    this.removeUserFromSelectedGroups = () => {
        const selectedGroups = this.gridApi.selection.getSelectedRows();
        ModalService.showMessageModal({
            messageText: 'Are you sure you want to remove this user from the selected group(s)?',
            pullCancelLeft: true,
            title: 'Remove User from Group(s)',
            windowClass: 'confirm-modal-sm',
            showOK: true
        }, () => {
            _.set(this.editData, 'roles', _.differenceBy(this.gridOptions.data, selectedGroups, 'id'));
            this.gridOptions.data = _.toArray(_.get(this.editData, 'roles'));
            this.editorForm.$setDirty();
        });
    };
    this.addUserToNewGroup = () => {
        ModalService.showGroupsModal(this.availableGroups, addUserToGroupsSelectionHandler)
    };

    const columnDefinitions = [
        {
            field: 'name',
            displayName: 'Name',
            sort: {
                direction: 'asc',
            },
            cellTemplate: 'components/visual/grid/padded-base-tooltip-template.html'
        }
    ];

    this.gridOptions = {
        rowSelection: true,
        enableRowHeaderSelection: true,
        enableSelectAll: true,
        enableColumnResizing: true,
        enablePaginationControls: false,
        enableSorting: true,
        enableColumnMenus: false,
        enableFullRowSelection: true,
        enableHorizontalScrollbar: uiGridConstants.ALWAYS,
        enableVerticalScrollbar: uiGridConstants.ALWAYS,
        rowHeight: 26,
        multiSelect: true,
        columnDefs: columnDefinitions,
        onRegisterApi: (gridApi: JS5Object) => {
            this.gridApi = gridApi;
        },
        rowTemplate: 'components/visual/grid/searchview-component-row-template.html'
    };

    this.toolbarButtonDefs = [
        {
            label: 'Save',
            enabled: () => {
                const passwordsMatch = _.isEqual(this.passwordInput, this.retypedPasswordInput);
                return this.editorForm.$dirty && _.size(_.get(this, 'emailUsernameInput')) &&
                    (this.requirePassword ? (this.passwordValid && this.retypedPasswordValid && passwordsMatch) : true)
            },
            onClick: saveHandler
        },
        {
            label: 'Revert',
            enabled: () => {
                return !_.get(this.editorForm, '$pristine');
            },
            onClick: () => {
                this.editData = _.cloneDeep(UsersService.originalEditData);
                this.gridOptions.data = _.get(this.editData, 'roles');
                this.gridOptions.data = [];
                this.editorForm.$setPristine();
                resetPasswordFields();
            }
        },
        {
            label: 'Close',
            enabled: () => !_.get(this.editorForm, '$dirty'),
            onClick: () => $location.url($route.current.rootRoute)
        }
    ];

    this.$onInit = () => {
        SidebarService.editService = UsersService;
        this.isAdmin = PermissionsService.isAdmin();
        this.loadingIndicator = {
            backdrop: true,
            show: false,
            title: 'Working',
            message: 'Please wait...'
        };
        resetPasswordFields();
        if (this.isAdmin) {
            let promises = [];
            this.loadingIndicator.show = true;
            if ($routeParams.id !== '-1') {
                promises.push(getData());
            }
            promises.push(getAvailableGroups());
            promises.push(UsersService.getPasswordRequirements().then((passwordRequirements: JS5Object) => {
                this.passwordRequirements = passwordRequirements;
            }));
            $q.all(promises).then(() => {
                this.loadingIndicator.show = false;
            })
        } else {
            $location.url('/login');
        }
    };
    const resetPasswordFields = () => {
        _.set(this, 'passwordInput', EMPTY_STRING);
        _.set(this, 'retypedPasswordInput', EMPTY_STRING);
    };
    this.$onDestroy = () => {
        UsersService.originalEditData = null;
        UsersService.viewData = null;
    };

    this.$postLink = () => {
        $timeout(() => {
            UsersService.editorForm = this.editorForm;
            BreadcrumbService.editService = UsersService;
        });
    };
}


angular.module('prism-ui.users').component('usersEditView', {
    templateUrl: 'system/users/editor/users-edit-view-template.html',
    controller: UsersEditViewController,
    bindings: {
    }
}).directive('noDirty', function () {
    return {
        require: 'ngModel',
        link: function (scope, element, attrs, ngModelCtrl: JS5Object) {
            // override the $setDirty method on ngModelController
            ngModelCtrl.$setDirty = angular.noop;
        }
    }
});
